import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Form, Tag, Button, Col, Row, SelectProps } from 'antd'
import { FieldTypeEnum } from '@/config/enum'
import { useGetState, useSetState } from 'ahooks'
import styles from './index.module.less'
import { LayoutEnum } from './enum'
import {
  filterEmptyValue,
  getDefaultValues,
  getSchemaItemByKey,
  getTagValue,
  isValidSearchValue,
} from './utils'
import clsx from 'clsx'
import { RedoOutlined } from '@ant-design/icons'
import userRender from './hooks/userRender'
import { produce } from 'immer'
import FilterInputSearch, { ChangeParamsType } from './components/FilterInput'

// 搜索配置项。根据配置项生成表单，下拉，日期，输入框等
export interface ConfigItem {
  key: string
  label: string
  name: string
  type?: FieldTypeEnum
  defaultValue?: any
  resetValue?: any
  options?: any[]
  allowClear?: boolean
  componentType: FieldTypeEnum
  render?: (value: any, record: any) => React.ReactNode
  placeholder?: string
  width?: number
  height?: number
  multiple?: SelectProps['mode']
  fieldNames?: Record<string, any>
  selectConfig?: SelectProps
  picker?: string // 'year' | 'month' | 'week' | 'date'
}

// 操作项
export interface OpType {
  position?: string // 操作项位置，左侧和右侧
  [key: string]: any
}

export interface SelectInputSetting {
  width?: number
  inputWidth?: number
  placeholder?: string
  selectWidth?: number
}

export enum ClickTypeEnum {
  expand = 1,
  collapse = 2,
  search = 3
}

export type ClickType = ClickTypeEnum | ''

export interface TableSearchProps {
  showCount?: number // 展示的个数
  config: ConfigItem[] // 搜索配置项
  onSearch?: (data: any) => void // 搜索回调
  needMore?: boolean // 是否需要跟多按钮
  showTag?: boolean // 是否需要展示搜索实时标签
  zbSearch?: string
  ops?: OpType[] // 操作项
  className?: string // 类名
  searchExtra?: React.ReactNode // 搜索 按钮后面的额外内容，比如高级搜索
  onChange?: (changedValues: any, allValues: any) => void
  TagSearch?: TagSearchProps
  showSelectSearch?: boolean // 是否展示下拉搜索框
  selectInputSetting?: SelectInputSetting
  onClick?: (type: ClickTypeEnum) => void
  groupInputFields?: boolean // 是否将 INPUT 类型字段聚集到复合搜索框，默认为 true
  leftFlexWrap?: 'nowrap' | 'wrap' // 左侧容器是否允许换行，默认为 nowrap
}

export interface TagSearchProps {
  attributes: ConfigItem[]
}

const TableSearch = ({
  config,
  onSearch,
  needMore,
  showTag,
  ops,
  className,
  onChange,
  onClick,
  searchExtra,
  showSelectSearch,
  selectInputSetting,
  showCount,
  zbSearch,
  groupInputFields = true, // 默认将 INPUT 字段聚集到复合搜索框
  leftFlexWrap = 'nowrap' // 默认不换行
}: TableSearchProps) => {
  const { renderSearchField, renderOp } = userRender()
  const [form] = Form.useForm()
  const [state, setState] = useSetState({
    tags: [] as any[],
    showMore: false,
  })

  // 表单值
  // const [formData, setFormData, getFormData] = useGetState<any>({})
  const formDataRef = useRef({})
  // schema 值
  const [schemas, setSchemas, getSchemas] = useGetState<ConfigItem[]>([])
  // 设置表单缓存数据
  const setFormDataRef = (values) => {
    formDataRef.current = { ...formDataRef.current, ...values }
  }
  const handleFormChange = (changedValues, allValues) => {
    setFormDataRef({ ...allValues })
    form.setFieldsValue(allValues)
    onChange?.(changedValues, allValues)
    const keys = Object.keys(changedValues)
    const changeTargetSchema = getSchemas().find(i => i.name === keys[0])
    if (changeTargetSchema?.componentType !== FieldTypeEnum.INPUT) {
      // console.log("changeTargetSchema", changeTargetSchema)
      handleSearch()
    }
    console.log("changedValues", changedValues)
    // handleSearch
  }

  // 搜索
  const handleSearch = async () => {
    // const values = form.getFieldsValue() // 获取不到隐藏的表单项
    const tags = Object.entries(formDataRef.current)
      .filter(([key, value]) => isValidSearchValue(value))
      .map(([key, value]) => ({
        key,
        value,
        label: schemas.find(item => item.key === key).label,
        desc: getTagValue(key, value, schemas),
      }))
    if (showTag) {
      setState({
        tags,
      })
    }
    // setState({
    //   showMore: false,
    // })
    const postData = filterEmptyValue(formDataRef.current)
    onSearch?.(postData)
    onClick?.(ClickTypeEnum.search)
  }

  // 移除标签
  const removeTag = key => {
    setState({
      tags: state.tags.filter(tag => tag.key !== key),
    })
    const field = getSchemaItemByKey(schemas, key)
    form.setFieldValue(key, field?.resetValue) // 没有就是 undefined
    const newSchema = produce(schemas, draft => {
      draft.find(item => item.key === key).defaultValue = field?.resetValue // 重置默认值
    })
    setSchemas(newSchema)
    setFormDataRef({ [key]: field?.resetValue })
    handleSearch() // 触发搜索
  }

  // 展开更多
  const handleMore = () => {
    setState({
      showMore: !state.showMore,
    })
    onClick?.(!state.showMore ? ClickTypeEnum.expand : ClickTypeEnum.collapse)
  }

  // 清空标签
  const handleClearSearch = () => {
    form.resetFields() // 重置一组字段到 initialValues
    formDataRef.current = getDefaultValues(schemas)
    setState({
      tags: [],
    })
    handleSearch() // 重置后搜索
  }

  // 搜索
  // const hanldeFilterInputChange = e => {
  //   console.log("hanldeFilterInputChange")
  //   console.log(e)
  // }
  const filterInputSchema = useMemo(() => {
    if (schemas.length > 0 && groupInputFields) {
      // 如果启用聚集功能，将所有 INPUT 类型的字段放入复合搜索框
      return schemas.filter(i => i.componentType === FieldTypeEnum.INPUT)
    }
    // 如果禁用聚集功能，返回空数组，INPUT 字段将平铺展示
    return []
  }, [schemas, groupInputFields])

  // console.log("selectInputSchema", filterInputSchema)

  const hanldeFilterInputSearch = (e: ChangeParamsType) => {
    const obj = {}
    filterInputSchema.map(i => {
      obj[i.key] = ""
    })
    obj[e.key] = e.value
    form.setFieldsValue(obj)
    setFormDataRef(obj)

    handleSearch()
  }

  // useEffect(() => {
  //   form.setFieldsValue(formDataRef.current)
  // }, [schemas])

  // 根据外部 config 更新内部 schema
  useEffect(() => {
    // formStata 填充默认值
    formDataRef.current = getDefaultValues(config)
    form.setFieldsValue(formDataRef.current)
    setSchemas(config)
  }, [config])


  const rightSchemas = useMemo(() => {
    // 复合框里的搜索框，不需要再出现在右侧
    const res = schemas.filter(item => !filterInputSchema.find(f => f.label === item.label))
    return res.slice(0, showCount || 3)
  }, [schemas, showCount, filterInputSchema])

  const leftOps = ops?.filter(op => !op.position || op.position === 'left') || []
  const rightOps = ops?.filter(op => op.position === 'right') || []
  // console.log("filterInputSchema", filterInputSchema)
  return (
    <div className={clsx(styles.container, className, { [styles.shadow]: state.showMore })}>
      <Form form={form} onValuesChange={handleFormChange}>
        {/* 搜索项 */}
        <div className={styles.fieldList}>
          <div className={styles.first}>
            <div className={clsx(styles.left, styles[leftFlexWrap])}>
              {/* 左侧操作项 */}
              {leftOps?.length > 0 ? (
                <div className={styles.actionList}>
                  {leftOps?.map(op => renderOp(op))}
                  {schemas?.length > 0 && <div className={styles.divider} />}
                </div>
              ) : null}
              {/* 下拉复合搜索框 */}
              {filterInputSchema.length > 0 ? <FilterInputSearch
                onSearch={hanldeFilterInputSearch}
                // onChange={hanldeFilterInputChange}
                schemas={filterInputSchema}
                selectInputSetting={selectInputSetting}
              /> : null}
              {zbSearch === 'zb' ? (
                <div className={styles.fieldItem}>
                  <Row className='w-full' gutter={[15, 15]}>
                    {schemas.slice(0, showCount || 3).map(field => (
                      <Col span={6} key={field.key}>
                        {renderSearchField(field, { layout: LayoutEnum.HORIZONTAL, filterInputSchema })}
                      </Col>
                    ))}
                  </Row>
                </div>
              ) :
                rightSchemas.map(field => (
                  <div className={styles.fieldItem} key={field.key}>
                    {renderSearchField(field, { layout: LayoutEnum.HORIZONTAL, filterInputSchema })}
                  </div>
                ))
              }
              {/* {!showSelectSearch ? <Button onClick={handleSearch} className={styles.fieldItem} type='primary' ghost>
                搜索
              </Button> : null} */}
              {searchExtra}
            </div>
            <div className={styles.setting}>
              {needMore ? (
                <Button onClick={handleMore} type='link' className={styles.fieldItem}>
                  {state.showMore ? '收起' : '展开'}筛选
                </Button>
              ) : null}
              {/* <Button onClick={handleClearSearch} icon={<RedoOutlined />} /> */}
              {/* 右侧操作项 */}
              {rightOps?.length > 0 ? (
                <div className={styles.actionListEnd}>{rightOps?.map(op => renderOp(op))}</div>
              ) : null}
            </div>
          </div>
          {state.showMore ? (
            <div className={styles.end}>
              <Row className='w-full' gutter={[15, 15]}>
                {schemas.slice(showCount || 3).map(field => (
                  <Col span={6} key={field.key}>
                    {renderSearchField(field, { layout: LayoutEnum.VERTICAL, filterInputSchema })}
                  </Col>
                ))}
              </Row>
              {/* 操作 */}
              <div className={styles.moreOpWrap}>
                <Button onClick={handleSearch} type='primary'>
                  确认
                </Button>
                <Button onClick={handleClearSearch} ghost type='primary'>
                  重置
                </Button>
                {/* <Button size='small' type='link' onClick={handleMore}>
                  收起搜索
                </Button> */}
              </div>
            </div>
          ) : null}
        </div>
      </Form>
      <div className={clsx(styles.tagWrap, { hidden: state.tags.length === 0 || state.showMore })}>
        {/* {JSON.stringify(state.tags)} */}
        {state.tags.map(tag => (
          <Tag key={tag.key} closable onClose={() => removeTag(tag.key)}>
            {`${tag.label}：${tag.desc}`}
          </Tag>
        ))}
        <span onClick={handleClearSearch} className={styles.clear}>
          清空
        </span>
      </div>
    </div>
  )
}

export default TableSearch
